import { AxiosResponse } from "axios";
import { RequestDetailsProps, RequestsProps } from "../@types/requests";
import { apinewforms } from "./api";

export const getRequestForStatus = (
  status:
    | "Aguardando"
    | "Solicitado"
    | "Em Andamento"
    | "Finalizado"
    | "Cancelado"
    | "Restituído"
    | "Encaminhado",
  filter: {
    page?: number;
    filtro?: string;
    search?: string;
    orgao?: string;
  }
): Promise<AxiosResponse<RequestsProps>> =>
  apinewforms.get(`/flow/solicitacoes/`, {
    params: { status, ...filter },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export const getRequestAll = (
  status: { value: string; label: string }[] | string[],
  filter: {
    page?: number;
    filtro?: string;
    search?: string;
    data?: string;
    orgao__slug?: string;
    modelo: string;
    servico: string;
    data_inicio: string;
    setor: { value: string; label: string }[] | string[];
    data_fim: string;
    titulo: string;
    protocolo: string;
    servico_slug: string;
  }
): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  const isValidValue = (value: any) =>
    value !== null && value !== undefined && value !== "";

  if (Array.isArray(status)) {
    status.forEach((s) => {
      const value = typeof s === "object" ? s.value : s;
      if (isValidValue(value)) {
        params.append("status", value);
      }
    });
  } else if (isValidValue(status)) {
    params.append("status", status);
  }

  if (Array.isArray(filter.setor)) {
    filter.setor.forEach((s) => {
      const value = typeof s === "object" ? s.value : s;
      if (isValidValue(value)) {
        params.append("setor", value);
      }
    });
  } else if (isValidValue(filter.setor)) {
    params.append("setor", filter.setor);
  }

  // Itera sobre os demais campos do filtro
  Object.entries(filter).forEach(([key, value]) => {
    // Exclui 'setor' e 'status' da verificação, pois já foram tratados
    if (key !== "setor" && key !== "status" && isValidValue(value)) {
      params.append(key, value.toString());
    }
  });

  return apinewforms.get(`/flow/relatorios/solicitacoes`, {
    params: params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

const isValidValue = (value: any): boolean =>
  value !== null && value !== undefined && value !== "";

const appendParams = (params, key, data) => {
  if (Array.isArray(data)) {
    data.forEach((item) => {
      const value = typeof item === "object" ? item.value : item;
      if (isValidValue(value)) {
        params.append(key, value);
      }
    });
  } else if (isValidValue(data)) {
    params.append(key, data);
  }
};

export const getHistoryServices = (status, filter) => {
  const params = new URLSearchParams();

  appendParams(params, "status", status);
  appendParams(params, "atendente", filter.atendente);
  appendParams(params, "status_atual", filter.status_atual);

  Object.entries(filter).forEach(([key, value]) => {
    if (!["atendente", "status", "status_atual"].includes(key)) {
      appendParams(params, key, value);
    }
  });

  return apinewforms.get(`/flow/historicos/solicitacoes`, {
    params: params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getReportsData = (filter: {
  page?: number;
  status?: string | string[];
  setor?: string | string[];
  cpf?: string;
  data_inicio?: string;
  data_fim?: string;
  flow__slug?: string;
  protocolo?: string;
  data?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const { status, setor, ...restOfFilters } = filter;

  const params = new URLSearchParams();

  Object.entries(restOfFilters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      if (key === "cpf" && typeof value === "string") {
        const cleanedCPF = value.replace(/\D/g, "");
        params.append(key, cleanedCPF);
      } else {
        params.append(key, value.toString());
      }
    }
  });

  if (status) {
    if (Array.isArray(status)) {
      status.forEach((s) => {
        if (s !== "") params.append("status", s);
      });
    } else if (status !== "") {
      params.append("status", status);
    }
  }

  if (setor) {
    if (Array.isArray(setor)) {
      setor.forEach((s) => {
        if (s !== "") params.append("setor", s);
      });
    } else if (setor !== "") {
      params.append("setor", setor);
    }
  }

  return apinewforms.get(`/flow/relatorios-csv/solicitacoes`, {
    params: params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getServicesDataCSV = (filter: {
  page?: number;
  status?: { value: string; label: string }[] | string[];
  status_atual?: { value: string; label: string }[] | string[];
  setor?: string;
  cpf?: string;
  data_inicio?: string;
  data_fim?: string;
  flow__slug?: string;
  protocolo?: string;
  data?: string;
  atendente: { value: string; label: string }[] | string[];
}): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  const isEmptyValue = (value: any) => {
    return (
      value === undefined ||
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" &&
        value !== null &&
        Object.keys(value).length === 0)
    );
  };

  if (!isEmptyValue(filter.status_atual)) {
    if (Array.isArray(filter.status_atual)) {
      filter.status_atual.forEach((s) => {
        const value = typeof s === "object" ? s.value : s;
        params.append("status_atual", value);
      });
    } else {
      params.append("status_atual", filter.status_atual);
    }
  }

  if (!isEmptyValue(filter.status)) {
    if (Array.isArray(filter.status)) {
      filter.status.forEach((s) => {
        const value = typeof s === "object" ? s.value : s;
        params.append("status", value);
      });
    } else {
      params.append("status", filter.status);
    }
  }

  if (!isEmptyValue(filter.atendente)) {
    if (Array.isArray(filter.atendente)) {
      filter.atendente.forEach((a) => {
        const value = typeof a === "object" ? a.value : a;
        params.append("atendente", value);
      });
    } else {
      params.append("atendente", filter.atendente);
    }
  }

  Object.entries(filter).forEach(([key, value]) => {
    if (
      !["atendente", "status", "status_atual"].includes(key) &&
      !isEmptyValue(value)
    ) {
      params.append(key, value.toString());
    }
  });

  return apinewforms.get(`/flow/relatorios-csv/atendimentos`, {
    params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getReportsDataSolicitacoesPDF = (filter: {
  status?: string;
  modelo?: boolean;
  servico_slug?: string;
  orgao?: string;
  cpf?: string;
  data_inicio?: string;
  flow__slug?: string;
  setor?: string;
  data_fim?: string;
  search?: string;
  protocolo?: string;
  data?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const { orgao, status, setor, ...restOfFilters } = filter;

  const params = new URLSearchParams();

  if (orgao) params.append("orgao__slug", orgao);

  if (status) {
    if (Array.isArray(status)) {
      status.forEach((s) => params.append("status", s));
    } else {
      params.append("status", status);
    }
  }
  if (setor) {
    if (Array.isArray(setor)) {
      setor.forEach((s) => params.append("setor", s));
    } else {
      params.append("setor", setor);
    }
  }

  Object.entries(restOfFilters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params.append(key, value.toString());
    }
  });

  return apinewforms.get("/flow/relatorios-pdf/solicitacoes/", {
    params: params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token") || ""}`,
    },
  });
};

export const getServicesDataXlsx = (filter: {
  page?: number;
  status?: { value: string; label: string }[] | string[];
  status_atual?: { value: string; label: string }[] | string[];
  atendente?: { value: string; label: string }[] | string[];
  modelo?: boolean;
  flow__slug?: string;
  orgao?: string;
  cpf?: string;
  data_inicio?: string;
  data_fim?: string;
  search?: string;
  protocolo?: string;
  data?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  const isValidValue = (value: any) => {
    return !(
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0)
    );
  };

  const appendIfValid = (key: string, value: any) => {
    if (isValidValue(value)) {
      const finalValue =
        typeof value === "object" && value.value ? value.value : value;
      params.append(key, finalValue.toString());
    }
  };

  ["status", "status_atual", "atendente"].forEach((field) => {
    const fieldValue = filter[field];
    if (Array.isArray(fieldValue)) {
      fieldValue.forEach((item) => appendIfValid(field, item));
    } else {
      appendIfValid(field, fieldValue);
    }
  });

  Object.entries(filter).forEach(([key, value]) => {
    if (!["status", "status_atual", "atendente"].includes(key)) {
      appendIfValid(key, value);
    }
  });

  return apinewforms.get(`/flow/relatorios-xlsx/atendimentos`, {
    params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getReportsDataXlsx = async (filter: {
  page?: number;
  status?: string | string[];
  modelo?: boolean;
  servico_slug?: string;
  flow__slug?: string;
  orgao?: string;
  cpf?: string;
  data_inicio?: string;
  setor?: string | string[];
  data_fim?: string;
  search?: string;
  protocolo?: string;
  data?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  const appendIfValid = (key: string, value: any) => {
    if (value !== null && value !== undefined && value !== "") {
      params.append(key, String(value));
    }
  };

  if (filter.status) {
    if (Array.isArray(filter.status)) {
      filter.status.forEach((s) => appendIfValid("status", s));
    } else {
      appendIfValid("status", filter.status);
    }
  }

  if (filter.setor) {
    if (Array.isArray(filter.setor)) {
      filter.setor.forEach((s) => appendIfValid("setor", s));
    } else {
      appendIfValid("setor", filter.setor);
    }
  }

  Object.entries(filter).forEach(([key, value]) => {
    if (
      !["status", "setor"].includes(key) &&
      value !== null &&
      value !== undefined &&
      value !== ""
    ) {
      if (key === "orgao") {
        params.append("orgao__slug", String(value));
      } else {
        appendIfValid(key, value);
      }
    }
  });

  return apinewforms.get(`/flow/relatorios-xlsx/solicitacoes`, {
    params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getServicesDataAtendimentosPDF = (filter: {
  status?: { value: string; label: string }[] | string[];
  atendente?: { value: string; label: string }[] | string[];
  status_atual?: { value: string; label: string }[] | string[];
  modelo?: boolean;
  flow__slug?: string;
  orgao?: string;
  cpf?: string;
  data_inicio?: string;
  data_fim?: string;
  data?: string;
  search?: string;
  protocolo?: string;
  created_at?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  const appendIfValid = (key: string, value: any) => {
    if (value !== null && value !== undefined && value !== "") {
      params.append(key, value.toString());
    }
  };

  if (Array.isArray(filter.status_atual)) {
    filter.status_atual.forEach((s) => {
      const value = typeof s === "object" ? s.value : s;
      params.append("status_atual", value);
    });
  } else {
    params.append("status_atual", filter.status_atual);
  }

  if (Array.isArray(filter.status)) {
    filter.status.forEach((s) => {
      const value = typeof s === "object" ? s.value : s;
      appendIfValid("status", value);
    });
  } else if (filter.status) {
    appendIfValid("status", filter.status);
  }

  if (Array.isArray(filter.atendente)) {
    filter.atendente.forEach((a) => {
      const value = typeof a === "object" ? a.value : a;
      appendIfValid("atendente", value);
    });
  } else if (filter.atendente) {
    appendIfValid("atendente", filter.atendente);
  }

  Object.entries(filter).forEach(([key, value]) => {
    if (key !== "atendente" && key !== "status") {
      // For arrays, handle each element.
      if (Array.isArray(value)) {
        value.forEach((val) => {
          if (typeof val === "object") appendIfValid(key, val.value);
          else appendIfValid(key, val);
        });
      } else {
        appendIfValid(key, value);
      }
    }
  });

  return apinewforms.get(`/flow/relatorios-pdf/atendimentos/`, {
    params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getServicesDataPDF = (filter: {
  page?: number;
  status?: { value: string; label: string }[] | string[];
  atendente?: { value: string; label: string }[] | string[];
  modelo?: boolean;
  flow__slug?: string;
  orgao?: string;
  cpf?: string;
  data_inicio?: string;
  data_fim?: string;
  search?: string;
  protocolo?: string;
  created_at?: string;
}): Promise<AxiosResponse<RequestsProps>> => {
  const params = new URLSearchParams();

  if (Array.isArray(filter.status)) {
    filter.status.forEach((s) => {
      const value = typeof s === "object" ? s.value : s;
      params.append("status", value);
    });
  } else {
    params.append("status", filter.status);
  }

  if (Array.isArray(filter.atendente)) {
    filter.atendente.forEach((a) => {
      const value = typeof a === "object" ? a.value : a;
      params.append("atendente", value);
    });
  } else {
    params.append("atendente", filter.atendente);
  }

  Object.entries(filter).forEach(([key, value]) => {
    if (key !== "atendente" && value !== undefined && key !== "status") {
      params.append(key, value.toString());
    }
  });
  return apinewforms.get(`/flow/relatorios-pdf/atendimentos`, {
    params,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });
};

export const getRequestForId = (
  identificador: string
): Promise<AxiosResponse<RequestDetailsProps>> =>
  apinewforms.get(`/flow/carregar_solicitacao`, {
    params: { identificador },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export const getDocumentsRequest = (
  id: string,
  conformidade: string
): Promise<AxiosResponse> =>
  apinewforms.get(
    `/flow/documentos-solicitacao/?identificador_solicitacao=${id}`,
    {
      params: { id, conformidade },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
      },
    }
  );

export interface SignatureVerificationProps {
  remaining_quantity: number;
  remaining_signatures: Record<string, number>;
  tramitation: boolean;
  signatures:
    | {
        id?: number;
        hash: string | null;
        perfil: string;
        assinante: {
          username: string;
          first_name: string;
        };
        created_at: string;
        updated_at?: string;
      }
    | {
        id?: number;
        hash: string | null;
        perfil: string;
        assinante: {
          username: string;
          first_name: string;
        };
        created_at: string;
        updated_at?: string;
      }[];
}

export const verificarAssinaturas = (id: string): Promise<AxiosResponse> =>
  apinewforms.get(`/flow/verificar-assinaturas`, {
    params: { id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export const getAtendentes = (): Promise<AxiosResponse> =>
  apinewforms.get(`flow/atendentes`, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export type DocumentIdProps = {
  id: string;
  solicitacao: {
    id: number;
    cpf: string;
    hash: string;
    identificador: string;
    user_data: {
      nome: string;
      cpf: string;
    };
  };
  documento_origem: {
    id: string;
    assunto: string;
  };
  arquivos_recebidos: any[]; // precisa ser definido;
  conteudo: string;
  conformidade: boolean;
  verificado_em: string;
  verificado_por: number;
  created_at: string;
  updated_at: string;
};

export const getDocumentById = (id: string, solicitacao_id: string) =>
  apinewforms.get<DocumentIdProps>(`/flow/documentos-solicitacao/${id}/`, {
    params: { identificador_solicitacao: solicitacao_id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

interface ChangeAttendant {
  requestId: number;
  newAttendantCpf: string;
}

export const changeAttendant = ({
  requestId,
  newAttendantCpf,
}: ChangeAttendant) =>
  apinewforms.put(
    `/flow/alterar-atendente/${requestId}/`,
    {
      atendente: newAttendantCpf,
    },
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
      },
    }
  );

export const putDocument = (
  id: string,
  conformidade: boolean,
  solicitacao_id: string
) =>
  apinewforms.put(
    `/flow/documentos-solicitacao/${id}/`,
    {
      id,
      conformidade,
    },
    {
      params: { identificador_solicitacao: solicitacao_id },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
      },
    }
  );

export const attachDocumentRequests = (
  solicitacao_id: string,
  formData: FormData
) =>
  apinewforms.post(`/flow/anexo-resposta/`, formData, {
    params: { solicitacao_id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export const attachDocumentRequestsIntern = (
  identificador_solicitacao: string,
  formData: FormData
) =>
  apinewforms.post(`/flow/anexos-internos/`, formData, {
    params: { identificador_solicitacao },
    headers: {
      Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
    },
  });

export const attachDocumentRestitutionRequests = (
  formData: FormData,
  identificador: string
) =>
  apinewforms.post(
    `/flow/pre-anexo-restituicao/?identificador_solicitacao=${identificador}`,
    formData,
    {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("gov_access_token")}`,
      },
    }
  );
